import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Creatable,
  Drawer,
  Input,
  Switch,
  useDrawer,
} from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { JsonInput } from '@/common/components/JsonInput'
import { useActiveGeneralSettings } from '@/common/hooks/useActiveGeneralSettings'
import { useNewApplication } from '../hooks/useNewApplication'
import {
  NewApplicationForm,
  NewApplicationSchema,
} from '../schemas/NewApplicationSchema'

export function DrawerNewApplication() {
  const { t } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-new-application')
  const hookForm = useForm<NewApplicationForm>({
    resolver: zodResolver(NewApplicationSchema()),
    defaultValues: {
      applicationKey: '',
      title: '',
      description: '',
      tag: [],
      auth: '',
      needsApiUser: false,
      needsEntityAccount: false,
      metadata: '',
    },
  })
  const { handleCreateApplication, isCreatingApplication } = useNewApplication()
  const { settings } = useActiveGeneralSettings()

  const handleSubmit = async (data: NewApplicationForm) => {
    await handleCreateApplication(data).then(() => {
      hookForm.reset()
      close()
    })
  }

  const handleCancelNewApplication = () => {
    hookForm.reset()
    close()
  }

  return (
    <Drawer.Root id="drawer-new-application">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:applications.drawer_new_application.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:applications.drawer_new_application.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <div className="flex gap-4">
              <div className="flex-1">
                <Input.Base
                  name="applicationKey"
                  label={t(
                    'sso:applications.drawer_new_application.fields.application_key.label',
                  )}
                  placeholder={t(
                    'sso:applications.drawer_new_application.fields.application_key.placeholder',
                  )}
                />
              </div>

              <div className="flex-1">
                <Input.Base
                  name="title"
                  label={t(
                    'sso:applications.drawer_new_application.fields.title.label',
                  )}
                  placeholder={t(
                    'sso:applications.drawer_new_application.fields.title.placeholder',
                  )}
                />
              </div>
            </div>

            <Input.Base
              name="description"
              label={t(
                'sso:applications.drawer_new_application.fields.description.label',
              )}
              placeholder={t(
                'sso:applications.drawer_new_application.fields.description.placeholder',
              )}
            />

            <Creatable.Sync
              name="tag"
              isMulti
              label={t(
                'sso:applications.drawer_new_application.fields.tag.label',
              )}
              placeholder={t(
                'sso:applications.drawer_new_application.fields.tag.placeholder',
              )}
            />

            <JsonInput
              name="auth"
              label={t(
                'sso:applications.drawer_new_application.fields.auth.label',
              )}
              placeholder={t(
                'sso:applications.drawer_new_application.fields.auth.placeholder',
              )}
            />

            <JsonInput
              name="metadata"
              label={t(
                'sso:applications.drawer_new_application.fields.metadata.label',
              )}
              placeholder={t(
                'sso:applications.drawer_new_application.fields.metadata.placeholder',
              )}
            />

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="needsApiUser">
                {t(
                  'sso:applications.drawer_new_application.fields.requires_api_user.label',
                )}
              </Switch.Label>

              <Switch.Trigger name="needsApiUser" />
            </Switch.Root>

            {settings?.enableAccountEntity && (
              <Switch.Root className="flex justify-between">
                <Switch.Label htmlFor="needsEntityAccount">
                  {t(
                    'sso:applications.drawer_new_application.fields.requires_entity_account.label',
                  )}
                </Switch.Label>

                <Switch.Trigger name="needsEntityAccount" />
              </Switch.Root>
            )}
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={handleCancelNewApplication}
            disabled={isCreatingApplication}
          >
            {t(
              'sso:applications.drawer_new_application.footer.close_button.label',
            )}
          </Button.Root>

          <Button.Root
            variant="primary"
            color="brand"
            onClick={hookForm.handleSubmit(handleSubmit)}
            type="submit"
            isLoading={isCreatingApplication}
          >
            {t(
              'sso:applications.drawer_new_application.footer.submit_button.label',
            )}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
